/**
 * iter547 · Reviewer Quick-Access Panel
 *
 * Renders directly beneath the golden UPGRADE CTA on the home screen,
 * for the Apple Review account ONLY (isReviewerAccount check). Every
 * App Store review guideline that requires a specific in-app surface
 * gets one tap-to-navigate row here, so the reviewer can verify each
 * one in under a minute without hunting through menus.
 *
 * Guidelines covered:
 *   2.1   In-App Purchases          → Paywall
 *   3.1.1 Restore Purchases         → Paywall (Restore button in footer)
 *   1.2   UGC — report objectionable→ Report Content demo
 *   1.2   UGC — block abusive users → Blocked Users manager
 *   5.1.1(v) Account deletion       → Delete Account modal deep-link
 *   5.1.1 Privacy Policy            → /privacy
 *   —     Terms of Service          → /terms
 *   —     Sign Out                  → useUserAuth().logout()
 *
 * Delete this file + the one call site in App.js `HomeIndex` and the
 * panel disappears. No other user ever sees it.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles,
  RotateCcw,
  Flag,
  ShieldOff,
  Trash2,
  ScrollText,
  FileText,
  LogOut,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { useUserAuth } from '../../contexts/UserAuthContext';
import { usePaywall } from '../../contexts/PaywallContext';
import { isReviewerAccount } from '../../lib/reviewerMode';

const API = process.env.REACT_APP_BACKEND_URL;

const ReviewerQuickAccessPanel = () => {
  const { user, logout, refreshUser } = useUserAuth();
  const { openPaywall } = usePaywall();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const [resetting, setResetting] = useState(false);

  if (!isReviewerAccount(user)) return null;

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      if (typeof logout === 'function') await logout();
    } catch { /* logout also nukes local state on failure */ }
    navigate('/');
  };

  const handleResetTrialState = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/reviewer/self-reset`,
        {},
        { withCredentials: true },
      );
      if (data?.ok) {
        toast.success('Reviewer state reset — paywall is fresh.', { duration: 6000 });
        if (typeof refreshUser === 'function') { try { await refreshUser(); } catch { /* noop */ } }
      } else {
        toast.error('Reset returned no confirmation. Try again.');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'unknown error';
      toast.error(`Reset failed: ${detail}`, { duration: 8000 });
    } finally {
      setResetting(false);
    }
  };

  const rows = [
    {
      id: 'paywall',
      icon: Sparkles,
      title: 'Subscription Paywall',
      subtitle: '5 tiers · Monthly + Annual · 7-day free trial',
      guideline: 'Guideline 2.1',
      onClick: () => openPaywall('bio'),
    },
    {
      id: 'restore',
      icon: RotateCcw,
      title: 'Restore Purchases',
      subtitle: 'Opens paywall — footer has Restore button',
      guideline: 'Guideline 3.1.1',
      onClick: () => openPaywall('bio'),
    },
    {
      id: 'report',
      icon: Flag,
      title: 'Report Objectionable Content',
      subtitle: 'Opens Ozzie Albies’ profile — tap ⋯ on any post',
      guideline: 'Guideline 1.2 · UGC',
      onClick: () => navigate('/network/ozzie-albies?reviewer-guide=report'),
    },
    {
      id: 'block',
      icon: ShieldOff,
      title: 'Manage Blocked Users',
      subtitle: 'Unblock or review blocked accounts',
      guideline: 'Guideline 1.2 · UGC',
      onClick: () => navigate('/me/blocked?reviewer-guide=block'),
    },
    {
      id: 'delete-account',
      icon: Trash2,
      title: 'Delete Account',
      subtitle: 'Permanent, one-tap-away from home',
      guideline: 'Guideline 5.1.1(v)',
      onClick: () => navigate('/profile?delete=1&reviewer-guide=delete'),
    },
    {
      id: 'privacy',
      icon: ScrollText,
      title: 'Privacy Policy',
      subtitle: 'Data collection + retention + your rights',
      guideline: 'Guideline 5.1.1',
      onClick: () => navigate('/privacy?reviewer-guide=privacy'),
    },
    {
      id: 'terms',
      icon: FileText,
      title: 'Terms of Service',
      subtitle: 'Subscription terms + EULA',
      onClick: () => navigate('/terms?reviewer-guide=terms'),
    },
    {
      id: 'reset-trial',
      icon: RefreshCw,
      title: resetting ? 'Resetting…' : 'Reset My Trial State',
      subtitle: 'Wipes premium flags on this account · fresh paywall',
      onClick: handleResetTrialState,
    },
    {
      id: 'sign-out',
      icon: LogOut,
      title: signingOut ? 'Signing out…' : 'Sign Out',
      subtitle: 'Clears the reviewer session on this device',
      onClick: handleSignOut,
      danger: true,
    },
  ];

  return (
    <div
      className="w-full px-4 pt-3"
      data-testid="reviewer-quick-access-wrapper"
    >
      <div
        className="rounded-2xl overflow-hidden ring-1 ring-white/10"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
          backdropFilter: 'blur(12px)',
        }}
        data-testid="reviewer-quick-access-panel"
      >
        <div className="px-5 pt-4 pb-3 border-b border-white/[0.06]">
          <p className="text-[10px] uppercase tracking-[0.32em] font-black text-amber-300/80 leading-none">
            Apple Review · Compliance Checklist
          </p>
          <p className="mt-2 text-[13px] text-white/70 leading-snug">
            Every feature Apple asked us to surface, one tap away. Verify each row.
          </p>
        </div>

        <ul className="divide-y divide-white/[0.06]">
          {rows.map((row) => {
            const Icon = row.icon;
            return (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={row.onClick}
                  disabled={(row.id === 'sign-out' && signingOut) || (row.id === 'reset-trial' && resetting)}
                  className="w-full flex items-center gap-3 px-5 py-3.5 text-left transition active:bg-white/[0.04] disabled:opacity-60"
                  data-testid={`reviewer-quick-access-${row.id}`}
                >
                  <span
                    className={`inline-flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0 ${
                      row.danger
                        ? 'bg-rose-500/12 text-rose-300'
                        : 'bg-white/[0.06] text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={2.4} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className={`text-[14px] font-bold leading-tight ${row.danger ? 'text-rose-200' : 'text-white'}`}>
                      {row.title}
                    </p>
                    <p className="mt-0.5 text-[11px] text-white/50 leading-tight truncate">
                      {row.subtitle}
                    </p>
                  </div>
                  {row.guideline && (
                    <span className="hidden xs:inline text-[10px] uppercase tracking-widest font-bold text-amber-300/70 flex-shrink-0">
                      {row.guideline}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-white/40 flex-shrink-0" strokeWidth={2.4} />
                </button>
              </li>
            );
          })}
        </ul>

        <div className="px-5 py-3 border-t border-white/[0.06] bg-black/20">
          <p className="text-[10px] text-white/40 leading-snug">
            This panel is visible only to the Apple Review account
            (<span className="text-white/60">applereview@flythouse.com</span>).
            Real users never see it.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReviewerQuickAccessPanel;
